// CRM Shell — the persistent layout for every CRM screen. The global CRM
// provider stack (QueryClient, theme, selected project, active call) is
// mounted in App.js by CrmGlobalProviders so an in-progress call survives
// navigation out of /crm/preview. This file owns only the in-shell view
// switcher and the CRM-scoped reminder engine.
//
// First-run: if the org has no CRM projects, the entire shell is replaced
// with NoProjectsWelcome. Every screen is project-scoped, so without one
// the UI is functionally empty — better to focus the user on creating one
// than to surface a half-broken shell.

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Leads from "./Leads";
import Pipeline from "./Pipeline";
import Settings from "./Settings";
import Dashboard from "./Dashboard";
import CommandCenter from "./CommandCenter";
import Inbox from "./Inbox";
import Activities from "./Activities";
import FollowUps from "./FollowUps";
import Onboarding from "./Onboarding";
import ManagerReview from "./ManagerReview";
import NoProjectsWelcome from "./NoProjectsWelcome";
import ReminderOverlay from "../organisms/ReminderOverlay";
import MetaPagePickerModal from "../organisms/MetaPagePickerModal";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { useCrmFirstRun } from "../contexts/CrmFirstRunContext";
import { useMyCrmRole } from "../hooks/useTeamMembers";

// 2026-08-30 (user): the CRM dashboard opens on CLASSIC by default. "dashboard"
// and "classic" both render the card-grid analytics screen; the Command
// Center (deal radar) lives at "radar", reachable from Classic's button.
const VALID_VIEWS = ["leads", "pipeline", "dashboard", "classic", "radar", "inbox", "activities", "followups", "workflows", "nurture", "onboarding", "review", "settings"];

// Each tab is a sidebar destination with its own URL (/dashboard/crm/leads).
// The legacy ?tab= form is still honoured because OAuth callbacks redirect
// back with it.
function viewFromQuery() {
  try {
    const tab = new URLSearchParams(window.location.search).get("tab");
    if (tab && VALID_VIEWS.includes(tab)) return tab;
  } catch (_e) {}
  return null;
}

// Handle OAuth callback query params (meet=connected, email_connect=connected,
// etc.) — toast the user, kick the relevant queries to refetch, then strip
// the params from the URL so a refresh doesn't re-fire the toast.
function useOAuthCallbackHandler() {
  const qc = useQueryClient();
  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    const meet = params.get("meet");
    const meetUser = params.get("meet_user");
    const emailConnect = params.get("email_connect");
    const meta = params.get("meta");
    if (!meet && !meetUser && !emailConnect && !meta) return;

    const email = params.get("email");
    const provider = params.get("provider");
    const errMsg = params.get("error");

    if (meet === "connected") {
      toast.success(`Connected ${email || "Google account"}`);
      qc.invalidateQueries({ queryKey: ["crm", "project-meet-account"] });
    } else if (meet === "error") {
      toast.error(errMsg || "Meet connect failed");
    }

    if (meetUser === "connected") {
      toast.success(`Your Google account connected${email ? `: ${email}` : ""}`);
      qc.invalidateQueries({ queryKey: ["crm", "user-meet-account"] });
    } else if (meetUser === "error") {
      toast.error(errMsg || "Google account connect failed");
    }

    if (emailConnect === "connected") {
      toast.success(`${provider || "Mailbox"} connected${email ? `: ${email}` : ""}`);
      qc.invalidateQueries({ queryKey: ["crm", "email-accounts"] });
    } else if (emailConnect === "error") {
      toast.error(errMsg || "Mailbox connect failed");
    }

    const pageName = params.get("pageName");
    if (meta === "connected") {
      toast.success(`Facebook Page connected${pageName ? `: ${pageName}` : ""}`);
      qc.invalidateQueries({ queryKey: ["crm", "project-meta-account"] });
    } else if (meta === "error") {
      toast.error(errMsg || "Facebook connect failed");
    }

    // Strip status params so a page refresh doesn't re-toast. Keep `tab` and
    // `projectId` though — Settings reads `projectId` to land on the right
    // project's row after a Meet connect; it strips the param itself once
    // consumed.
    const keep = new URLSearchParams();
    const tab = params.get("tab");
    const projectId = params.get("projectId");
    if (tab) keep.set("tab", tab);
    if (projectId) keep.set("projectId", projectId);
    const newQuery = keep.toString();
    const url = window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
    try { window.history.replaceState({}, "", url); } catch (_e) {}
  // Run once on mount — we only react to params present at load time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function useMetaPagePickerState() {
  const [pickerState, setPickerState] = useState(null);

  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    const meta = params.get("meta");
    const projectId = params.get("projectId");
    if (meta !== "select" || !projectId) return;

    const pid = params.get("pid");
    const pendingToken = params.get("pendingToken");

    // Strip picker params from URL immediately.
    const keep = new URLSearchParams();
    const tab = params.get("tab");
    if (tab) keep.set("tab", tab);
    if (projectId) keep.set("projectId", projectId);
    const newQuery = keep.toString();
    const url = window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
    try { window.history.replaceState({}, "", url); } catch (_e) {}

    if (pid) {
      // New flow: fetch pages from backend using pid (avoids large URL header).
      import("../api/client").then(({ crmApi }) => {
        crmApi.get(`/auth/meta/pending-pages`, { params: { pid } })
          .then((r) => {
            const pages = r.data?.pages || [];
            if (pages.length > 0) setPickerState({ projectId, pid, pages });
          })
          .catch(() => { /* session expired — user will reconnect */ });
      });
    } else if (pendingToken) {
      // Legacy fallback: decode from JWT directly.
      try {
        const payload = JSON.parse(atob(pendingToken.split(".")[1]));
        const pages = (payload.pages || []).map((p) => ({ id: p.id, name: p.name }));
        if (pages.length > 0) setPickerState({ projectId, pendingToken, pages });
      } catch { /* ignore malformed token */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [pickerState, () => setPickerState(null)];
}

function ShellInner() {
  const { tab } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [pipelineIdleFilter, setPipelineIdleFilter] = useState("all");
  const [metaPicker, clearMetaPicker] = useMetaPagePickerState();
  const { isAdmin: isCrmAdmin, isLoading: roleLoading } = useMyCrmRole();
  useOAuthCallbackHandler();

  const view = (tab && VALID_VIEWS.includes(tab) && tab) || viewFromQuery() || "dashboard";

  // "/dashboard/crm" with or without a tab segment — derived so the CRM module
  // never hardcodes the host app's dashboard base path.
  const crmBase = location.pathname.replace(/\/crm(\/[^/]*)?\/?$/, "/crm");
  const setView = useCallback((v) => {
    navigate(v === "dashboard" ? crmBase : `${crmBase}/${v}`);
  }, [navigate, crmBase]);

  // Deep-link guard: the Review tab is manager-only (backend 403s otherwise).
  // If a non-admin lands on /crm/review via URL, bounce them to the dashboard
  // once the role resolves so they can't bypass the hidden-tab gate.
  useEffect(() => {
    if (!roleLoading && !isCrmAdmin && view === "review") {
      navigate(crmBase, { replace: true });
    }
  }, [roleLoading, isCrmAdmin, view, navigate, crmBase]);

  const navigateToPipeline = (idleFilter) => {
    setPipelineIdleFilter(idleFilter || "all");
    setView("pipeline");
  };

  const navigateToReview = () => setView("review");

  function renderView(v) {
    switch (v) {
      case "leads":
        return <Leads />;
      case "pipeline":
        return <Pipeline idleFilterProp={pipelineIdleFilter} onIdleFilterChange={setPipelineIdleFilter} />;
      case "dashboard":
      case "classic":
        return <Dashboard onNavigateToPipeline={navigateToPipeline} onNavigateToReview={navigateToReview} onNavigateToRadar={() => setView("radar")} />;
      case "radar":
        return <CommandCenter onNavigate={setView} onNavigateToPipeline={navigateToPipeline} />;
      case "inbox":
        return <Inbox />;
      case "activities":
        return <Activities />;
      case "followups":
        return <FollowUps />;
      // Workflows moved into Settings → Projects → Workflows because they are
      // per-project. Both old paths land there rather than 404-ing to the
      // dashboard: Settings opens on Projects, which opens on Workflows.
      case "workflows":
      case "nurture":
        return <Settings embedded />;
      case "onboarding":
        return <Onboarding />;
      case "review":
        return <ManagerReview />;
      case "settings":
        return <Settings embedded />;
      default:
        return <Leads />;
    }
  }
  const { projects, isLoading } = useSelectedProject();
  const { membersOnboarding } = useCrmFirstRun();

  if (isLoading) {
    return (
      <div className="crm-shell" style={{
        flex: 1, minHeight: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--color-bg-base)", color: "var(--color-text-secondary)",
        gap: 10, fontSize: 14,
      }}>
        <Loader2 size={16} className="animate-spin" /> Loading CRM…
      </div>
    );
  }

  if (projects.length === 0 || membersOnboarding) {
    return (
      <div className="crm-shell" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: "var(--color-bg-base)", color: "var(--color-text-primary)" }}>
        <NoProjectsWelcome />
      </div>
    );
  }

  return (
    <div className="crm-shell" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden", background: "var(--color-bg-base)", color: "var(--color-text-primary)" }}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {renderView(view)}
      </div>
      {metaPicker && (
        <MetaPagePickerModal
          projectId={metaPicker.projectId}
          pid={metaPicker.pid}
          pendingToken={metaPicker.pendingToken}
          pages={metaPicker.pages}
          onDone={clearMetaPicker}
        />
      )}
    </div>
  );
}

export default function Preview() {
  return (
    <>
      <ShellInner />
      <ReminderOverlay />
    </>
  );
}
